import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Shield, Users, Radio, Trophy, CalendarClock, ArrowRight, Zap } from "lucide-react";
import JoinDiscordCard from "@/components/JoinDiscordCard";
import TransferTicker from "@/components/TransferTicker";

export default function Dashboard() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [fixtures, setFixtures] = useState([]);
  const [results, setResults] = useState([]);
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/overview").then((r) => setOverview(r.data)),
      api.get("/fixtures?status=upcoming").then((r) => setFixtures(r.data.slice(0, 4))),
      api.get("/results").then((r) => setResults(r.data.slice(0, 4))),
      api.get("/teams").then((r) => setTeams(r.data)),
    ]).catch(() => {});
  }, []);

  const teamById = (id) => teams.find((t) => t.id === id);
  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-10" data-testid="dashboard-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className={`font-mono tracking-[0.3em] text-xs mb-2 ${isAdmin ? "text-[#FF007F]" : "text-cyan-400"}`}>
            / {isAdmin ? "COMMAND CENTER" : "MEMBER TERMINAL"}
          </div>
          <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">
            {isAdmin ? "Admin, welcome." : `Good to see you, ${user?.name || "operator"}.`}
          </h1>
          <p className="text-gray-400 mt-2 flex items-center gap-2">
            {isAdmin ? <Shield className="w-4 h-4 text-[#FF007F]" /> : <Zap className="w-4 h-4 text-cyan-400" />}
            {isAdmin ? "You have full control over the circuit." : "Track your league, follow the moves, catch the streams."}
          </p>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dashboard-metrics">
        {[
          { label: "Teams", val: overview?.teams, icon: Users, color: "text-cyan-400" },
          { label: "Athletes", val: overview?.players, icon: Trophy, color: "text-[#FF007F]" },
          { label: "Streamers", val: overview?.streamers, icon: Radio, color: "text-[#F4D03F]" },
          { label: "Upcoming", val: overview?.upcoming_fixtures, icon: CalendarClock, color: "text-white" },
        ].map((m) => (
          <div key={m.label} className="border border-[#232330] bg-[#101015] p-5">
            <m.icon className={`w-4 h-4 mb-3 ${m.color}`} />
            <div className="font-heading font-black text-4xl">{m.val ?? "—"}</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mt-1">{m.label}</div>
          </div>
        ))}
      </div>

      {/* Iter49 · Transfer ticker (silent when empty) */}
      <TransferTicker />

      {/* Iter48 · One-tap Discord community invite (hides itself when unset) */}
      <JoinDiscordCard />

      {/* Live modules */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="border border-[#232330] bg-[#101015] p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl font-black uppercase tracking-tight">Upcoming</h2>
            <Link to="/schedule" className="text-xs text-cyan-400 uppercase tracking-widest hover:underline">Full schedule</Link>
          </div>
          <ul className="space-y-3">
            {fixtures.map((f) => {
              const h = teamById(f.home_team_id), a = teamById(f.away_team_id);
              return (
                <li key={f.id} className="flex items-center justify-between border-b border-[#232330] pb-3 last:border-none">
                  <div>
                    <div className="font-mono text-xs text-gray-500">{new Date(f.scheduled_at).toLocaleString()}</div>
                    <div className="font-heading font-bold uppercase tracking-tight">
                      {h?.name} <span className="text-gray-500">vs</span> {a?.name}
                    </div>
                  </div>
                  <span className="text-xs text-cyan-400 font-mono">{f.game}</span>
                </li>
              );
            })}
            {fixtures.length === 0 && <li className="text-gray-500 text-sm">No fixtures scheduled.</li>}
          </ul>
        </div>

        <div className="border border-[#232330] bg-[#101015] p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl font-black uppercase tracking-tight">Latest results</h2>
            <Link to="/results" className="text-xs text-cyan-400 uppercase tracking-widest hover:underline">All results</Link>
          </div>
          <ul className="space-y-3">
            {results.map((r) => {
              const h = teamById(r.home_team_id), a = teamById(r.away_team_id);
              const homeWin = r.home_score > r.away_score;
              return (
                <li key={r.id} className="flex items-center justify-between border-b border-[#232330] pb-3 last:border-none">
                  <div>
                    <div className="font-heading font-bold uppercase tracking-tight">
                      <span className={homeWin ? "text-[#FF007F]" : "text-gray-300"}>{h?.tag}</span>
                      <span className="font-mono text-white mx-3">{r.home_score}–{r.away_score}</span>
                      <span className={!homeWin && r.away_score !== r.home_score ? "text-[#FF007F]" : "text-gray-300"}>{a?.tag}</span>
                    </div>
                    <div className="text-xs text-gray-500 font-mono mt-1">{r.game}</div>
                  </div>
                </li>
              );
            })}
            {results.length === 0 && <li className="text-gray-500 text-sm">No results yet.</li>}
          </ul>
        </div>
      </div>

      {isAdmin && (
        <div className="border border-[#FF007F]/40 bg-[#FF007F]/5 p-6">
          <div className="text-xs text-[#FF007F] uppercase tracking-[0.2em] font-bold mb-4">Admin tools</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { to: "/admin", label: "Admin panel" },
              { to: "/fixtures", label: "Submit results" },
              { to: "/transfers", label: "Register transfer" },
              { to: "/streamers", label: "Verify streamers" },
            ].map((a) => (
              <Link
                key={a.to}
                to={a.to}
                data-testid={`admin-tool-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center justify-between border border-[#232330] px-4 py-3 hover:border-[#FF007F] hover:bg-black transition-colors"
              >
                <span className="text-sm font-bold uppercase tracking-widest">{a.label}</span>
                <ArrowRight className="w-4 h-4 text-[#FF007F]" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
